import { Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import React, { useEffect, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";

import { clearMessage } from "../../../redux/actions/message";

import ProtectedRoutes from "../../../routes/ProtectedRoutes";
import PublicRoutes from "../../../routes/PublicRoutes";
import { useHideUnimportantErrors } from "../../../hooks/useHideUnimportantErrors";
import ProductVariant from "../../Product/Barcode";
import DashboardContainer from "../../Dashboard/AdminDashboard";
import PermissionLayout from "../../Auth/PermissionLayout";
import PermissionRoute from "../../Auth/PermissionRoute";
import SuperAdminRoute from "../../Auth/SuperAdminRoute";

const Login = lazy(() => import("../../Login/"));
const Profile = lazy(() => import("../../Profile"));
const Dashboard = lazy(() => import("../../Dashboard/"));
const SalesEarningsDashboard = lazy(() => import("../../Dashboard/SalesEarningsDashboard"));
const ForgotPassword = lazy(() => import("../../ForgotPassword/"));
const ResetPassword = lazy(() => import("../../ResetPassword/"));

const UserList = lazy(() => import("../../Users/List"));
const AddUser = lazy(() => import("../../Users/AddUser"));
const AddStoreAdmin = lazy(() => import("../../Users/AddStoreAdmin"));
const User = lazy(() => import("../../Users/User"));

const ProductList = lazy(() => import("../../Product/List"));
const AddProduct = lazy(() => import("../../Product/AddProduct"));
const Product = lazy(() => import("../../Product/Product"));
const FruiteList = lazy(() => import("../../Product/FruiteList"));

const AddCampaign = lazy(() => import("../../Campaign/AddCampaign"));
const CampaignList = lazy(() => import("../../Campaign/List"));
const Campaign = lazy(() => import("../../Campaign/Campaign"));

const AddBanner = lazy(() => import("../../Banner/AddBannerInformation"));
const BinnerList = lazy(() => import("../../Banner/List"));
const Banner = lazy(() => import("../../Banner/Banner"));


const RewardList = lazy(() => import("../../Reward/WinnerPreviewList"));

const SaleList = lazy(() => import("../../Sales/SaleList"));
const AddSale = lazy(() => import("../../Sales/AddSale"));
const SaleView = lazy(() => import("../../Sales/SaleView"));

const CategoryList = lazy(() => import("../../Categories/List"));
const AddCategory = lazy(() => import("../../Categories/AddCategory"));
const Category = lazy(() => import("../../Categories/Category"));


const OrderList = lazy(() => import("../../Order/List"));
const Order = lazy(() => import("../../Order/Order"));

const FruiteOrders = lazy(() => import("../../Order/FruiteOrderList"));

const RefundList = lazy(() => import("../../Refund/List"));
const Refund = lazy(() => import("../../Refund/Refund"));

const ContactList = lazy(() => import("../../Contact/List"));

const CartList = lazy(() => import("../../Cart/List"));
const Cart = lazy(() => import("../../Cart/Cart"));

const ChatList = lazy(() => import("../../Chat/List"));
const ChatView = lazy(() => import("../../Chat/View"));

const SearchLogList = lazy(() => import("../../SearchLogs/List"));

const ReferralList = lazy(() => import("../../Referral/List"));

// Referral System Components
const ReferralSettings = lazy(() => import("../../ReferralSystem/Settings"));
const PendingUserApprovals = lazy(() => import("../../ReferralSystem/PendingUserApprovals"));
const EligibleUsers = lazy(() => import("../../ReferralSystem/EligibleUsers"));
const PendingRewards = lazy(() => import("../../ReferralSystem/PendingRewards"));
const ReferralStats = lazy(() => import("../../ReferralSystem/Stats"));
const UserReferralNetwork = lazy(() => import("../../ReferralSystem/UserReferralNetwork"));

const ProductReportList = lazy(() => import("../../Reports/List"));
const UserEarningsList = lazy(() => import("../../Reports/UserEarningsList"))
const CustomEarningsList = lazy(() => import("../../Reports/CustomEarningsChart"))

const CashBack = lazy(() => import("../../CashBack/CashBack"));

const DailyEntryForm = lazy(() => import("../../DailyLedger/DailyEntryForm"));
const DailyEntryHistory = lazy(() => import("../../DailyLedger/DailyEntryHistory"));
const ShopLedgerDashboard = lazy(() => import("../../DailyLedger/ShopLedgerDashboard"));

const PageNotFound = lazy(() => import("../../PageNotFound"));
const ForbiddenPage = lazy(() => import("../../Forbidden"))

const SettingsConfig = lazy(() => import("../../Settings/Config"));
const SettingsGeneral = lazy(() => import("../../Settings/General"));
const PermissionManagement = lazy(() => import("../../Settings/PermissionManagement"));

const StoreList = lazy(() => import("../../Stores/StoreList"));
const StoreDetail = lazy(() => import("../../Stores/StoreDetail"));
const PincodeList = lazy(() => import("../../Pincodes/PincodeList"));
const RatingsModerationList = lazy(() => import("../../Ratings/RatingsModerationList"));
const ActivityLogList = lazy(() => import("../../ActivityLogs/ActivityLogList"));
const PriceHistoryList = lazy(() => import("../../PriceHistory/PriceHistoryList"));
const PriceHistoryAnalytics = lazy(() => import("../../PriceHistory/PriceHistoryAnalytics"));
const InventoryAnalytics = lazy(() => import("../../InventoryAnalytics/InventoryAnalytics"));
const StaffList = lazy(() => import("../../Staff/StaffList"));
const StaffCreate = lazy(() => import("../../Staff/StaffCreate"));
const StaffDetail = lazy(() => import("../../Staff/StaffDetail"));

const PayrollRuns = lazy(() => import("../../Payroll/PayrollRuns"));
const PayrollRunDetail = lazy(() => import("../../Payroll/PayrollRunDetail"));
const PayrollAttendance = lazy(() => import("../../Payroll/Attendance"));
const PayrollHolidays = lazy(() => import("../../Payroll/Holidays"));
const PayrollSalaryConfig = lazy(() => import("../../Payroll/SalaryConfig"));
const PayrollAdvances = lazy(() => import("../../Payroll/Advances"));
const PayrollAdvanceDetail = lazy(() => import("../../Payroll/AdvanceDetail"));
const PayrollEmployeeBreakdown = lazy(() => import("../../Payroll/EmployeeBreakdown"));
const PayrollMyAttendance = lazy(() => import("../../Payroll/MyAttendance"));
const PayrollMyBreakdown = lazy(() => import("../../Payroll/MyBreakdown"));
const PayrollMoneyHub = lazy(() => import("../../Payroll/MoneyHub"));

const AppMain = () => {

  let { user: isLoggedIn, user: currentUser } = useSelector(
    (state) => state.auth
  );

  // isLoggedIn = true;
  const dispatch = useDispatch();

  let location = useLocation();

  useEffect(() => {
    if (["/login"].includes(location.pathname)) {
      dispatch(clearMessage()); // clear message when changing location
    }
  }, [dispatch, location]);
  useHideUnimportantErrors();
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route element={<ProtectedRoutes isLoggedIn={isLoggedIn} />}>
          <Route path="forbidden" element={<ForbiddenPage />} />
          <Route element={<PermissionLayout module="dashboard" />}>
            <Route
              path="dashboard"
              element={
                <SuperAdminRoute>
                  <Dashboard />
                </SuperAdminRoute>
              }
            />
            <Route
              path="admin/dashboard"
              element={
                <SuperAdminRoute>
                  <DashboardContainer />
                </SuperAdminRoute>
              }
            />
            <Route
              path="admin/inventory-analytics"
              element={
                <SuperAdminRoute>
                  <InventoryAnalytics />
                </SuperAdminRoute>
              }
            />
            <Route
              path="admin/dashboard/sales-earnings"
              element={
                <PermissionRoute module="analytics_dashboard" action="read">
                  <SalesEarningsDashboard />
                </PermissionRoute>
              }
            />
            <Route path="reports/earning-by-product" element={<ProductReportList currentUser={currentUser} />} />
            <Route path="reports/earning-by-user" element={<UserEarningsList currentUser={currentUser} />} />
            <Route path="reports/custom" element={<CustomEarningsList currentUser={currentUser} />} />
          </Route>
          <Route path="profile" element={<Profile />} />

          <Route element={<PermissionLayout module="staff" />}>
            <Route path="staff/list" element={<StaffList />} />
            <Route path="staff/create" element={<StaffCreate />} />
            <Route path="staff/:publicId" element={<StaffDetail />} />
          </Route>

          <Route element={<PermissionLayout module="payroll" />}>
            <Route path="payroll">
              <Route
                path="runs"
                element={
                  <PermissionRoute module="payroll" action="update">
                    <PayrollRuns />
                  </PermissionRoute>
                }
              />
              <Route
                path="runs/:id"
                element={
                  <PermissionRoute module="payroll" action="update">
                    <PayrollRunDetail />
                  </PermissionRoute>
                }
              />
              <Route
                path="attendance"
                element={
                  <PermissionRoute module="payroll" action="update">
                    <PayrollAttendance />
                  </PermissionRoute>
                }
              />
              <Route
                path="holidays"
                element={
                  <PermissionRoute module="payroll" action="update">
                    <PayrollHolidays />
                  </PermissionRoute>
                }
              />
              <Route
                path="salary"
                element={
                  <PermissionRoute module="payroll" action="update">
                    <PayrollSalaryConfig />
                  </PermissionRoute>
                }
              />
              <Route
                path="money"
                element={
                  <PermissionRoute module="payroll" action="update">
                    <PayrollMoneyHub />
                  </PermissionRoute>
                }
              />
              <Route
                path="advances"
                element={
                  <PermissionRoute module="payroll" action="read">
                    <PayrollAdvances />
                  </PermissionRoute>
                }
              />
              <Route
                path="advances/:id"
                element={
                  <PermissionRoute module="payroll" action="read">
                    <PayrollAdvanceDetail />
                  </PermissionRoute>
                }
              />
              <Route
                path="employee/:id/breakdown"
                element={
                  <PermissionRoute module="payroll" action="update">
                    <PayrollEmployeeBreakdown />
                  </PermissionRoute>
                }
              />
              <Route path="me/attendance" element={<PayrollMyAttendance />} />
              <Route path="me/money" element={<PayrollMyBreakdown />} />
              <Route path="me/breakdown" element={<PayrollMyBreakdown />} />
            </Route>
          </Route>

          <Route element={<PermissionLayout module="store" />}>
            <Route path="store">
              <Route path="list" element={<StoreList currentUser={currentUser} />} />
              <Route path=":storeId" element={<StoreDetail currentUser={currentUser} />} />
            </Route>
            <Route path="pincode">
              <Route path="list" element={<PincodeList currentUser={currentUser} />} />
            </Route>
          </Route>

          <Route element={<PermissionLayout module="user" />}>
            <Route path="user">
              <Route path="list" element={<UserList currentUser={currentUser} />} />
              <Route
                path="add/shopadmin"
                element={
                  <PermissionRoute module="user" action="create">
                    <AddStoreAdmin currentUser={currentUser} />
                  </PermissionRoute>
                }
              />
              <Route
                path="add"
                element={
                  <PermissionRoute module="user" action="create">
                    <AddUser currentUser={currentUser} />
                  </PermissionRoute>
                }
              />
              <Route path=":id" element={<User currentUser={currentUser} />} />
            </Route>
          </Route>

          <Route element={<PermissionLayout module="activity_log" />}>
            <Route path="activity-logs" element={<ActivityLogList currentUser={currentUser} />} />
            <Route path="activity-logs/actor/:publicId" element={<ActivityLogList currentUser={currentUser} />} />
          </Route>

          <Route element={<PermissionLayout module="price_history" />}>
            <Route path="price-history" element={<PriceHistoryList currentUser={currentUser} />} />
            <Route path="price-history/:scope/:publicId" element={<PriceHistoryList currentUser={currentUser} />} />
            <Route
              path="price-history/:scope/:publicId/analytics"
              element={<PriceHistoryAnalytics currentUser={currentUser} />}
            />
          </Route>

          <Route element={<PermissionLayout module="product" />}>
            <Route
              path="admin/ratings"
              element={
                <SuperAdminRoute>
                  <RatingsModerationList />
                </SuperAdminRoute>
              }
            />
            <Route path="product">
              <Route path="list" element={<ProductList currentUser={currentUser} />} />
              <Route path="add" element={<AddProduct currentUser={currentUser} />} />
              <Route path=":id" element={<Product currentUser={currentUser} />} />
              <Route path="barcode/:id" element={<ProductVariant currentUser={currentUser} />} />
            </Route>
            <Route path="fruite">
              <Route path="list" element={<FruiteList currentUser={currentUser} />} />
            </Route>
            <Route path="reward">
              <Route path="campaign/list" element={<CampaignList currentUser={currentUser} />} />
              <Route path="campaign/add" element={<AddCampaign />} />
              <Route path="campaign/:id" element={<Campaign currentUser={currentUser} />} />
              <Route path="winner/preview/:rewardCompanionId" element={<RewardList />} />
            </Route>
            <Route path="banners">
              <Route path="list" element={<BinnerList currentUser={currentUser} />} />
              <Route path="add" element={<AddBanner />} />
              <Route path=":id" element={<Banner currentUser={currentUser} />} />
            </Route>
          </Route>

          <Route path="sales">
            <Route
              path="list"
              element={
                <PermissionRoute module="sale" action="read">
                  <SaleList currentUser={currentUser} />
                </PermissionRoute>
              }
            />
            <Route
              path="add"
              element={
                <PermissionRoute module="sale" action="create">
                  <AddSale currentUser={currentUser} />
                </PermissionRoute>
              }
            />
            <Route
              path="view/:publicId"
              element={
                <PermissionRoute module="sale" action="read">
                  <SaleView currentUser={currentUser} />
                </PermissionRoute>
              }
            />
            <Route
              path=":id"
              element={
                <PermissionRoute module="sale" action="read">
                  <Product currentUser={currentUser} />
                </PermissionRoute>
              }
            />
          </Route>
          <Route path="sale">
            <Route
              path="list"
              element={
                <PermissionRoute module="sale" action="read">
                  <SaleList currentUser={currentUser} />
                </PermissionRoute>
              }
            />
            <Route
              path="add"
              element={
                <PermissionRoute module="sale" action="create">
                  <AddSale currentUser={currentUser} />
                </PermissionRoute>
              }
            />
            <Route
              path="view/:publicId"
              element={
                <PermissionRoute module="sale" action="read">
                  <SaleView currentUser={currentUser} />
                </PermissionRoute>
              }
            />
          </Route>

          <Route element={<PermissionLayout module="category" />}>
            <Route path="category">
              <Route path="list" element={<CategoryList currentUser={currentUser} />} />
              <Route path="add" element={<AddCategory currentUser={currentUser} />} />
              <Route path=":id" element={<Category currentUser={currentUser} />} />
            </Route>
          </Route>

          <Route element={<PermissionLayout module="order" />}>
            <Route path="order">
              <Route path="list" element={<OrderList currentUser={currentUser} />} />
              <Route path=":id" element={<Order currentUser={currentUser} />} />
            </Route>
            <Route path="fruit">
              <Route path="orders" element={<FruiteOrders currentUser={currentUser} />} />
            </Route>
            <Route path="search-logs">
              <Route path="list" element={<SearchLogList currentUser={currentUser} />} />
            </Route>
          </Route>

          <Route element={<PermissionLayout module="refund" />}>
            <Route path="refund">
              <Route path="list" element={<RefundList currentUser={currentUser} />} />
              <Route path=":id" element={<Refund currentUser={currentUser} />} />
            </Route>
          </Route>

          <Route element={<PermissionLayout module="support" />}>
            <Route path="contact">
              <Route path="list" element={<ContactList currentUser={currentUser} />} />
            </Route>
            <Route path="referrals">
              <Route path="list" element={<ReferralList currentUser={currentUser} />} />
            </Route>
            <Route path="referral">
              <Route path="settings" element={<ReferralSettings currentUser={currentUser} />} />
              <Route path="users/pending" element={<PendingUserApprovals currentUser={currentUser} />} />
              <Route path="users/eligible" element={<EligibleUsers currentUser={currentUser} />} />
              <Route path="users/:userId/network" element={<UserReferralNetwork currentUser={currentUser} />} />
              <Route path="rewards/pending" element={<PendingRewards currentUser={currentUser} />} />
              <Route path="stats" element={<ReferralStats currentUser={currentUser} />} />
            </Route>
            <Route path="cashback">
              <Route path="" element={<CashBack currentUser={currentUser} />} />
            </Route>
          </Route>

          <Route element={<PermissionLayout module="cart" />}>
            <Route path="cart">
              <Route path="list" element={<CartList currentUser={currentUser} />} />
              <Route path=":id" element={<Cart currentUser={currentUser} />} />
            </Route>
          </Route>

          <Route element={<PermissionLayout module="chat" />}>
            <Route path="chat">
              <Route path="list" element={<ChatList currentUser={currentUser} />} />
              <Route path=":userId" element={<ChatView currentUser={currentUser} />} />
            </Route>
          </Route>

          <Route path="daily-ledger">
            <Route
              path="add"
              element={
                <PermissionRoute module="daily_entry" action="create">
                  <DailyEntryForm currentUser={currentUser} />
                </PermissionRoute>
              }
            />
            <Route
              path="history"
              element={
                <PermissionRoute module="daily_entry" action="read">
                  <DailyEntryHistory currentUser={currentUser} />
                </PermissionRoute>
              }
            />
            <Route
              path="dashboard"
              element={
                <PermissionRoute module="daily_entry" action="read">
                  <ShopLedgerDashboard currentUser={currentUser} />
                </PermissionRoute>
              }
            />
          </Route>

          <Route element={<PermissionLayout module="permission" />}>
            <Route path="settings">
              <Route path="general" element={<SettingsGeneral currentUser={currentUser} />} />
              <Route path="config" element={<SettingsConfig currentUser={currentUser} />} />
              <Route
                path="permissions"
                element={
                  <SuperAdminRoute>
                    <Outlet />
                  </SuperAdminRoute>
                }
              >
                <Route index element={<Navigate to="overview" replace />} />
                <Route path=":tab" element={<PermissionManagement />} />
              </Route>
            </Route>
          </Route>

        </Route>
        <Route element={<PublicRoutes isLoggedIn={isLoggedIn} />}>
          <Route path="login" element={<Login />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Login />} />
          <Route path="*" element={<PageNotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppMain;
